/**
 * HardCopyTrackingDialog - Multi-purpose dialog for hard-copy lifecycle transitions.
 * Handles: create, print, provide, record-signature, upload-signed-copy, cancel.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FileText, Printer, Send, PenTool, Upload, XCircle, Loader2, AlertCircle, Search, User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import hardCopyTrackingAPI from '@/services/api/hardCopyTracking';
import type { HardCopyTrackingRecord, CreateTrackingRequest } from '@/types/hardCopyTracking';

export type DialogAction =
  | 'create'
  | 'print'
  | 'provide'
  | 'record-signature'
  | 'upload-signed-copy'
  | 'cancel';

interface HardCopyTrackingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: DialogAction;
  trackingRecord?: HardCopyTrackingRecord | null;
  /** For 'create' action: prefill data */
  createDefaults?: Partial<CreateTrackingRequest>;
  onComplete?: (record: HardCopyTrackingRecord) => void;
}

const ACTION_CONFIG: Record<DialogAction, {
  title: string;
  description: string;
  icon: any;
  submitLabel: string;
}> = {
  create: {
    title: 'Create Hard-Copy Tracking',
    description: 'Generate a tracking number for a physical document that needs to be printed, signed, and uploaded.',
    icon: FileText,
    submitLabel: 'Create & Generate Number',
  },
  print: {
    title: 'Record Print',
    description: 'Confirm that this document has been printed.',
    icon: Printer,
    submitLabel: 'Confirm Printed',
  },
  provide: {
    title: 'Record Provided for Signature',
    description: 'Record that this document was given to the member for review and signature.',
    icon: Send,
    submitLabel: 'Confirm Provided',
  },
  'record-signature': {
    title: 'Record Member Signature',
    description: 'Enter the date the member signed the physical document.',
    icon: PenTool,
    submitLabel: 'Record Signature',
  },
  'upload-signed-copy': {
    title: 'Upload Signed Copy',
    description: 'Upload the scanned signed document.',
    icon: Upload,
    submitLabel: 'Upload & Complete',
  },
  cancel: {
    title: 'Cancel Tracking',
    description: 'Cancel this hard-copy tracking record.',
    icon: XCircle,
    submitLabel: 'Confirm Cancel',
  },
};

// ============================================================================
// HELPERS
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

interface MemberOption {
  id: number;
  name: string;
}

interface DocumentOption {
  id: number;
  doc_type_name: string;
  created_at: string;
}

interface FormInstanceOption {
  id: number;
  template_name: string;
  status: string;
}

interface EnrollmentOption {
  id: number;
  program_name: string;
  program_id: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function HardCopyTrackingDialog({
  open,
  onOpenChange,
  action,
  trackingRecord,
  createDefaults,
  onComplete,
}: HardCopyTrackingDialogProps) {
  const [notes, setNotes] = useState('');
  const [providedDate, setProvidedDate] = useState('');
  const [signedDate, setSignedDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create-specific state
  const [memberSearch, setMemberSearch] = useState('');
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [selectedMemberName, setSelectedMemberName] = useState<string>('');
  const [memberDocs, setMemberDocs] = useState<DocumentOption[]>([]);
  const [memberForms, setMemberForms] = useState<FormInstanceOption[]>([]);
  const [memberEnrollments, setMemberEnrollments] = useState<EnrollmentOption[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
  const [linkType, setLinkType] = useState<'document' | 'form' | 'none'>('none');
  const [searchingMembers, setSearchingMembers] = useState(false);

  const config = ACTION_CONFIG[action];
  const Icon = config.icon;

  // Pre-fill from defaults
  useEffect(() => {
    if (open && action === 'create' && createDefaults) {
      if (createDefaults.target_id) {
        setSelectedMemberId(createDefaults.target_id);
        // Fetch member name
        fetchMemberName(createDefaults.target_id);
        fetchMemberData(createDefaults.target_id);
      }
      if (createDefaults.doc_record_id) {
        setSelectedDocId(createDefaults.doc_record_id);
        setLinkType('document');
      }
      if (createDefaults.form_instance_id) {
        setSelectedFormId(createDefaults.form_instance_id);
        setLinkType('form');
      }
      if (createDefaults.enrollment_id) {
        setSelectedEnrollmentId(createDefaults.enrollment_id);
      }
    }
  }, [open, action, createDefaults]);

  const fetchMemberName = async (memberId: number) => {
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/members/${memberId}`), {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedMemberName(`${data.first_name} ${data.last_name}`);
      }
    } catch { /* silent */ }
  };

  const searchMembers = useCallback(async (query: string) => {
    if (query.length < 2) { setMembers([]); return; }
    try {
      setSearchingMembers(true);
      const res = await fetch(getWeaveinApiUrl(`/api/members?search=${encodeURIComponent(query)}&page_size=10`), {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.items || data.members || [];
        setMembers(list.map((m: any) => ({
          id: m.id,
          name: `${m.first_name} ${m.last_name}`,
        })));
      }
    } catch { /* silent */ }
    finally { setSearchingMembers(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (memberSearch) searchMembers(memberSearch); }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, searchMembers]);

  const fetchMemberData = async (memberId: number) => {
    // Fetch docs
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/documents/member/${memberId}?page_size=50`), {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const docs = Array.isArray(data) ? data : [];
        setMemberDocs(docs.map((d: any) => ({
          id: d.id,
          doc_type_name: d.doc_type_name || 'Document',
          created_at: d.created_at,
        })));
      }
    } catch { /* silent */ }

    // Fetch enrollments
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/members/${memberId}/enrollments?status=active`), {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const enrollments = Array.isArray(data) ? data : [];
        setMemberEnrollments(enrollments.map((e: any) => ({
          id: e.id,
          program_name: e.program_name || `Program #${e.program_id}`,
          program_id: e.program_id,
        })));
      }
    } catch { /* silent */ }

    // Fetch form instances
    try {
      const res = await fetch(getWeaveinApiUrl(`/api/forms/instances?member_id=${memberId}&page_size=50`), {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const forms = Array.isArray(data) ? data : data.items || [];
        setMemberForms(forms.map((f: any) => ({
          id: f.id,
          template_name: f.template_name || f.form_template_name || `Form #${f.id}`,
          status: f.status || 'draft',
        })));
      }
    } catch { /* silent */ }
  };

  const selectMember = (member: MemberOption) => {
    setSelectedMemberId(member.id);
    setSelectedMemberName(member.name);
    setMemberSearch('');
    setMembers([]);
    setSelectedDocId(null);
    setSelectedFormId(null);
    setSelectedEnrollmentId(null);
    fetchMemberData(member.id);
  };

  const resetForm = () => {
    setNotes('');
    setProvidedDate('');
    setSignedDate('');
    setCancelReason('');
    setFile(null);
    setError(null);
    // Only reset create fields if no defaults
    if (!createDefaults?.target_id) {
      setMemberSearch('');
      setMembers([]);
      setSelectedMemberId(null);
      setSelectedMemberName('');
      setMemberDocs([]);
      setMemberForms([]);
      setMemberEnrollments([]);
      setSelectedDocId(null);
      setSelectedFormId(null);
      setSelectedEnrollmentId(null);
      setLinkType('none');
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);

    try {
      let result: HardCopyTrackingRecord;

      switch (action) {
        case 'create':
          if (!selectedMemberId) {
            throw new Error('Please select a member');
          }
          result = await hardCopyTrackingAPI.create({
            target_type: createDefaults?.target_type || 'member',
            target_id: selectedMemberId,
            doc_record_id: linkType === 'document' ? (selectedDocId || undefined) : undefined,
            form_instance_id: linkType === 'form' ? (selectedFormId || undefined) : undefined,
            enrollment_id: selectedEnrollmentId || undefined,
            program_id: selectedEnrollmentId
              ? memberEnrollments.find(e => e.id === selectedEnrollmentId)?.program_id
              : undefined,
            notes: notes || undefined,
          });
          break;

        case 'print':
          result = await hardCopyTrackingAPI.recordPrint(trackingRecord!.id, {
            notes: notes || undefined,
          });
          break;

        case 'provide':
          result = await hardCopyTrackingAPI.recordProvide(trackingRecord!.id, {
            provided_date: providedDate || undefined,
            notes: notes || undefined,
          });
          break;

        case 'record-signature':
          if (!signedDate) {
            throw new Error('Signature date is required');
          }
          result = await hardCopyTrackingAPI.recordSignature(trackingRecord!.id, {
            member_signed_at: signedDate,
            notes: notes || undefined,
          });
          break;

        case 'upload-signed-copy':
          if (!file) {
            throw new Error('Please select a file to upload');
          }
          result = await hardCopyTrackingAPI.uploadSignedCopy(
            trackingRecord!.id,
            file,
            notes || undefined,
          );
          break;

        case 'cancel':
          if (!cancelReason.trim()) {
            throw new Error('Cancellation reason is required');
          }
          result = await hardCopyTrackingAPI.cancel(trackingRecord!.id, {
            reason: cancelReason,
          });
          break;

        default:
          throw new Error('Unknown action');
      }

      resetForm();
      onOpenChange(false);
      onComplete?.(result);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {config.title}
          </DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Tracking number display for existing records */}
          {trackingRecord && (
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
              <span className="text-xs text-muted-foreground">Tracking #:</span>
              <span className="font-mono font-medium text-sm">{trackingRecord.tracking_number}</span>
            </div>
          )}

          {/* ============ CREATE-SPECIFIC FIELDS ============ */}
          {action === 'create' && (
            <>
              {/* Member search */}
              <div>
                <Label>Member *</Label>
                {selectedMemberId ? (
                  <div className="flex items-center justify-between p-2.5 border rounded-md bg-muted/30">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{selectedMemberName}</span>
                      <span className="text-xs text-muted-foreground">#{selectedMemberId}</span>
                    </div>
                    {!createDefaults?.target_id && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => {
                          setSelectedMemberId(null);
                          setSelectedMemberName('');
                          setMemberDocs([]);
                          setMemberForms([]);
                          setMemberEnrollments([]);
                          setSelectedDocId(null);
                          setSelectedFormId(null);
                          setSelectedEnrollmentId(null);
                        }}
                      >
                        Change
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="pl-9"
                    />
                    {searchingMembers && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                    {members.length > 0 && (
                      <div className="absolute z-10 top-full mt-1 w-full bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-48 overflow-auto">
                        {members.map((m) => (
                          <button
                            key={m.id}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                            onClick={() => selectMember(m)}
                          >
                            <User className="w-3.5 h-3.5 text-muted-foreground" />
                            {m.name}
                            <span className="text-xs text-muted-foreground ml-auto">#{m.id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Enrollment selection */}
              {selectedMemberId && memberEnrollments.length > 0 && (
                <div>
                  <Label>Enrollment (optional)</Label>
                  <Select
                    value={selectedEnrollmentId?.toString() || 'none'}
                    onValueChange={(v) => setSelectedEnrollmentId(v === 'none' ? null : parseInt(v))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select enrollment..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No enrollment</SelectItem>
                      {memberEnrollments.map((e) => (
                        <SelectItem key={e.id} value={e.id.toString()}>
                          {e.program_name} (#{e.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Link type: document or form */}
              {selectedMemberId && (
                <div>
                  <Label>Link to (optional)</Label>
                  <Select
                    value={linkType}
                    onValueChange={(v) => {
                      setLinkType(v as any);
                      setSelectedDocId(null);
                      setSelectedFormId(null);
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No link</SelectItem>
                      <SelectItem value="document">Document record</SelectItem>
                      <SelectItem value="form">Form instance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Document selector */}
              {linkType === 'document' && memberDocs.length > 0 && (
                <div>
                  <Label>Document</Label>
                  <Select
                    value={selectedDocId?.toString() || ''}
                    onValueChange={(v) => setSelectedDocId(parseInt(v))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select document..." /></SelectTrigger>
                    <SelectContent>
                      {memberDocs.map((d) => (
                        <SelectItem key={d.id} value={d.id.toString()}>
                          {d.doc_type_name} — {new Date(d.created_at).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Form instance selector */}
              {linkType === 'form' && memberForms.length > 0 && (
                <div>
                  <Label>Form Instance</Label>
                  <Select
                    value={selectedFormId?.toString() || ''}
                    onValueChange={(v) => setSelectedFormId(parseInt(v))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select form..." /></SelectTrigger>
                    <SelectContent>
                      {memberForms.map((f) => (
                        <SelectItem key={f.id} value={f.id.toString()}>
                          {f.template_name} ({f.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {/* ============ PROVIDE FIELDS ============ */}
          {action === 'provide' && (
            <div>
              <Label>Date Provided (optional, defaults to now)</Label>
              <Input
                type="date"
                value={providedDate}
                onChange={(e) => setProvidedDate(e.target.value)}
              />
            </div>
          )}

          {/* ============ RECORD SIGNATURE FIELDS ============ */}
          {action === 'record-signature' && (
            <div>
              <Label>Date Member Signed *</Label>
              <Input
                type="date"
                value={signedDate}
                onChange={(e) => setSignedDate(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                The actual date the member signed the physical document.
              </p>
            </div>
          )}

          {/* ============ UPLOAD SIGNED COPY FIELDS ============ */}
          {action === 'upload-signed-copy' && (
            <div>
              <Label>Signed Document *</Label>
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(file.size / 1048576).toFixed(1)} MB)
                    </span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click to select the scanned signed document
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, JPG, PNG up to 50MB
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.heic"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          )}

          {/* ============ CANCEL FIELDS ============ */}
          {action === 'cancel' && (
            <div>
              <Label>Reason for Cancellation *</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this tracking being cancelled?"
                rows={3}
              />
            </div>
          )}

          {/* Notes field (all actions except cancel) */}
          {action !== 'cancel' && (
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes..."
                rows={2}
              />
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 p-2 bg-red-50 rounded-md">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (action === 'create' && !selectedMemberId)}
            variant={action === 'cancel' ? 'destructive' : 'default'}
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {config.submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
